// GraphQL operations for the Generaciones tab (live generation status).
//
// Each frontend feature owns its own operations file (see graphql/projects.ts
// and graphql/project-detail.ts). This file backs the Generations view, which
// tracks asynchronous generation jobs (agent course structure, episode audio,
// episode video) as their status changes in the background.
//
// The view is driven by the `onGenerationRequestUpdated(projectId)`
// subscription: whenever a GenerationRequest changes state, AppSync fans the
// update out to subscribed project members (Req 6.6, 6.7). Because a
// subscription only carries CHANGES — not a historical snapshot — the client
// accumulates the updates it receives, keyed by generationRequestId, and
// renders the latest state of each request (Req 6.1 surfaces the request from
// its first PENDING publish onward).
//
// NOTE (initial historical list): the schema exposes only a single-item
// `getGenerationRequest(projectId, generationRequestId)` query and no list
// query. There is therefore no way to seed the view with requests created
// before the client subscribed; those only reappear if they change state again
// while subscribed. Populating the initial list would require a list query
// (e.g. `listGenerationRequests(projectId)`) added server-side later; the view
// is written so that adding such a seed later is a drop-in enhancement.
//
// Requirements 6.1, 6.6. Design → GraphQL Schema
// (Subscription.onGenerationRequestUpdated, Query.getGenerationRequest,
// type GenerationRequest) and Frontend (Generations view).

import { gql } from '@apollo/client';
import type { GenerationRequest } from '@courseai/domain';

/**
 * The GenerationRequest fields the Generaciones view renders: identity, kind
 * and status (for the live badge), the error description shown on FAILED
 * requests (Req 6.6), and timestamps used for ordering/labels. The optional
 * `courseId`/`episodeIndex` help describe which piece of work a request is for.
 */
export type GenerationRequestView = Pick<
  GenerationRequest,
  | 'projectId'
  | 'generationRequestId'
  | 'kind'
  | 'status'
  | 'courseId'
  | 'episodeIndex'
  | 'errorDescription'
  | 'createdAt'
  | 'updatedAt'
>;

// The selection set shared by the subscription and the single-item query so
// both deliver the same GenerationRequestView shape.
const GENERATION_REQUEST_FIELDS = `
  projectId
  generationRequestId
  kind
  status
  courseId
  episodeIndex
  errorDescription
  createdAt
  updatedAt
`;

/** Variables for the `onGenerationRequestUpdated` subscription. */
export interface OnGenerationRequestUpdatedVariables {
  readonly projectId: string;
}

/** Shape of each `onGenerationRequestUpdated` subscription payload. */
export interface OnGenerationRequestUpdatedData {
  readonly onGenerationRequestUpdated: GenerationRequestView | null;
}

/**
 * Subscribe to live generation-status updates for a project. AppSync runs the
 * project-membership check on connect and filters the fan-out so only members
 * of `projectId` receive updates (Req 6.6, 6.7). Each payload is the full,
 * current GenerationRequest after a state change.
 */
export const ON_GENERATION_REQUEST_UPDATED = gql`
  subscription OnGenerationRequestUpdated($projectId: ID!) {
    onGenerationRequestUpdated(projectId: $projectId) {
      ${GENERATION_REQUEST_FIELDS}
    }
  }
`;

/** Variables for the `getGenerationRequest` query. */
export interface GetGenerationRequestVariables {
  readonly projectId: string;
  readonly generationRequestId: string;
}

/** Shape of the `getGenerationRequest` query result. */
export interface GetGenerationRequestData {
  readonly getGenerationRequest: GenerationRequestView | null;
}

/**
 * Load a single GenerationRequest by id. The schema offers no list query, so
 * this only fetches one known request (membership enforced server-side). It is
 * exported for completeness and future use (e.g. refreshing a specific request
 * on demand); the live view is driven by the subscription above.
 */
export const GET_GENERATION_REQUEST = gql`
  query GetGenerationRequest($projectId: ID!, $generationRequestId: ID!) {
    getGenerationRequest(projectId: $projectId, generationRequestId: $generationRequestId) {
      ${GENERATION_REQUEST_FIELDS}
    }
  }
`;

/** Variables for the `myGenerationRequests` list query. */
export interface MyGenerationRequestsVariables {
  readonly projectId: string;
}

/** Shape of the `myGenerationRequests` query result. */
export interface MyGenerationRequestsData {
  readonly myGenerationRequests: ReadonlyArray<GenerationRequestView>;
}

/**
 * List ALL generation requests for a project (newest first). This seeds the
 * Generaciones view with existing/historical generations — the subscription
 * above only delivers live CHANGES, so without this a client opening the tab
 * after a job finished would see nothing. The live subscription continues to
 * layer real-time updates on top of this initial snapshot.
 */
export const MY_GENERATION_REQUESTS = gql`
  query MyGenerationRequests($projectId: ID!) {
    myGenerationRequests(projectId: $projectId) {
      ${GENERATION_REQUEST_FIELDS}
    }
  }
`;
